import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Search, Copy, Check, Camera, CalendarDays, X, Image as ImageIcon } from 'lucide-react';
import { Table } from '@/components';
import { orderApi } from '@/services/orderApi';
import { Order } from '@/types/order';
import toast from 'react-hot-toast';

type StatusFilter =
  | 'all'
  | 'pending'
  | 'preparing'
  | 'on_the_way'
  | 'delivered'
  | 'cancelled'
  | 'awaiting_bank_transfer'
  | 'awaiting_payment'
  | 'payment_failed';

const STATUS_LABELS: Record<string, string> = {
  all: 'Tüm Siparişler',
  pending: 'Beklemede',
  preparing: 'Hazırlanıyor',
  on_the_way: 'Kurye Yolda',
  delivered: 'Teslim Edildi',
  cancelled: 'İptal',
  awaiting_bank_transfer: 'Havale Bekleniyor',
  awaiting_payment: 'Ödeme Bekleniyor',
  payment_failed: 'Ödeme Başarısız',
};

const STATUS_COLORS: Record<string, { badge: string }> = {
  awaiting_payment: { badge: 'bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300 border border-orange-200 dark:border-orange-800' },
  awaiting_bank_transfer: { badge: 'bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300 border border-teal-200 dark:border-teal-800' },
  payment_failed: { badge: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300 border border-red-200 dark:border-red-800' },
  pending: { badge: 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800' },
  confirmed: { badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800' },
  preparing: { badge: 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800' },
  on_the_way: { badge: 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300 border border-purple-200 dark:border-purple-800' },
  delivered: { badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800' },
  cancelled: { badge: 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800' },
};

// Helper function to get date string in YYYY-MM-DD format
const getDateString = (daysOffset: number = 0): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().split('T')[0];
};

export const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [startDate, setStartDate] = useState<string>(getDateString(-7)); // Default: 7 gün öncesi
  const [endDate, setEndDate] = useState<string>(getDateString(1)); // Default: 1 gün sonrası
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [previewModalOrder, setPreviewModalOrder] = useState<Order | null>(null);

  const [stats, setStats] = useState({
    all: 0,
    pending: 0,
    preparing: 0,
    on_the_way: 0,
    delivered: 0,
    cancelled: 0,
    awaiting_bank_transfer: 0,
  });

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await orderApi.getOrders({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        searchQuery: searchQuery || undefined,
        limit: 100,
      });

      const fetchedOrders = response.orders || [];
      setOrders(fetchedOrders);

      // Stats hesapla
      const newStats = {
        all: fetchedOrders.length,
        pending: 0,
        preparing: 0,
        on_the_way: 0,
        delivered: 0,
        cancelled: 0,
        awaiting_bank_transfer: 0,
      };

      fetchedOrders.forEach((order) => {
        if (order.status in newStats) {
          newStats[order.status as keyof typeof newStats]++;
        }
      });

      setStats(newStats);
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();

    const handleNewOrder = () => {
      loadOrders();
    };

    window.addEventListener('orders:new', handleNewOrder);
    return () => {
      window.removeEventListener('orders:new', handleNewOrder);
    };
  }, [statusFilter, startDate, endDate, searchQuery]);

  const handleCopyOrderNo = (e: React.MouseEvent, orderNo: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(orderNo);
    setCopiedId(orderNo);
    toast.success(`Sipariş no kopyalandı: ${orderNo}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const setPresetDateRange = (type: 'today' | 'yesterday' | 'week' | 'month' | 'all') => {
    if (type === 'today') {
      setStartDate(getDateString(0));
      setEndDate(getDateString(1));
    } else if (type === 'yesterday') {
      setStartDate(getDateString(-1));
      setEndDate(getDateString(0));
    } else if (type === 'week') {
      setStartDate(getDateString(-7));
      setEndDate(getDateString(1));
    } else if (type === 'month') {
      setStartDate(getDateString(-30));
      setEndDate(getDateString(1));
    } else if (type === 'all') {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleOpenDatePicker = (e: React.MouseEvent<HTMLInputElement>) => {
    try {
      e.currentTarget.showPicker?.();
    } catch (_) {
      // Ignore if not supported
    }
  };

  const tableData = orders.map((order) => {
    const previewPhoto = order.orderPreview?.photoUrl || order.orderPreview?.photoUrls?.[0];
    const hasPreview = Boolean(previewPhoto);

    let deliveryDateText = '-';
    if (order.items?.[0]?.deliveryDate) {
      deliveryDateText = new Date(order.items[0].deliveryDate).toLocaleDateString('tr-TR');
      if (order.items[0].deliveryTime) {
        deliveryDateText += ` (${order.items[0].deliveryTime})`;
      }
    }

    return {
      id: order.id,
      orderNo: order.orderNo || order.id,
      rawOrder: order,
      senderName: order.senderInfo?.name || 'Belirtilmedi',
      senderPhone: order.senderInfo?.phone || '',
      senderEmail: order.senderInfo?.email || '',
      recipientName: order.recipientInfo?.fullName || (order.recipientInfo?.firstName ? `${order.recipientInfo?.firstName || ''} ${order.recipientInfo?.lastName || ''}` : 'Misafir Alıcı'),
      recipientPhone: order.recipientInfo?.phone || '',
      recipientAddress: order.recipientInfo?.address || order.deliveryAreaName || '',
      itemCount: `${order.items?.length || 0} Ürün`,
      itemTitle: order.items?.[0]?.title || 'Çiçek Siparişi',
      deliveryDateText,
      isDelivered: order.status === 'delivered',
      hasPreview,
      previewPhoto,
      payment: (() => {
        const method = order.paymentInfo?.method;
        if (method === 'credit-card') return 'Kredi Kartı';
        if (method === 'bank-transfer') {
          const bankName = (order.paymentInfo as any)?.bankTransferAccountSnapshot?.bankName;
          return bankName ? `Havale (${bankName})` : 'Havale';
        }
        return method || '-';
      })(),
      total: `₺${(order.totalPrice || 0).toFixed(2)}`,
      status: STATUS_LABELS[order.status] || order.status,
      statusRaw: order.status,
      date: new Date(order.createdAt).toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  });

  const statusTabItems: { id: StatusFilter; label: string; count?: number; color: string }[] = [
    { id: 'all', label: 'Tüm Siparişler', count: stats.all, color: 'border-slate-500 text-slate-700 dark:text-slate-200' },
    { id: 'pending', label: 'Beklemede', count: stats.pending, color: 'border-amber-500 text-amber-600 dark:text-amber-400' },
    { id: 'preparing', label: 'Hazırlanıyor', count: stats.preparing, color: 'border-blue-500 text-blue-600 dark:text-blue-400' },
    { id: 'on_the_way', label: 'Kurye Yolda', count: stats.on_the_way, color: 'border-purple-500 text-purple-600 dark:text-purple-400' },
    { id: 'delivered', label: 'Teslim Edildi', count: stats.delivered, color: 'border-emerald-500 text-emerald-600 dark:text-emerald-400' },
    { id: 'awaiting_bank_transfer', label: 'Havale Bekleyen', count: stats.awaiting_bank_transfer, color: 'border-teal-500 text-teal-600 dark:text-teal-400' },
    { id: 'cancelled', label: 'İptal Edildi', count: stats.cancelled, color: 'border-rose-500 text-rose-600 dark:text-rose-400' },
  ];

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-slate-100 tracking-tight flex items-center space-x-2">
            <span>Sipariş Yönetimi</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Gelen siparişleri takip edin, teslimat durumunu ve onay fotoğraflarını yönetin.
          </p>
        </div>
      </div>

      {/* Status Filter Tabs (Compact Metric Bar) */}
      <div className="flex items-center space-x-1.5 overflow-x-auto pb-1.5 pt-0.5 no-scrollbar border-b border-slate-200 dark:border-slate-800">
        {statusTabItems.map((tab) => {
          const isActive = statusFilter === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`flex items-center space-x-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
                isActive
                  ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-md scale-[1.02]'
                  : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800'
              }`}
            >
              <span>{tab.label}</span>
              {typeof tab.count === 'number' && (
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                    isActive
                      ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Multi-Field Search & Date Filters */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          {/* Main Search Input */}
          <div className="md:col-span-6 relative">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">
              Sipariş No / Müşteri / Alıcı / Telefon Ara
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Örn: #260908, Sonfer DER, 0544..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs pl-9 pr-8 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Date Pickers (Clickable everywhere) */}
          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">
              Başlangıç Tarihi
            </label>
            <div
              className="relative cursor-pointer"
              onClick={() => {
                const el = document.getElementById('startDateInput') as HTMLInputElement;
                el?.showPicker?.();
              }}
            >
              <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
              <input
                id="startDateInput"
                type="date"
                value={startDate}
                onClick={handleOpenDatePicker}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-rose-500/50"
              />
            </div>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">
              Bitiş Tarihi
            </label>
            <div
              className="relative cursor-pointer"
              onClick={() => {
                const el = document.getElementById('endDateInput') as HTMLInputElement;
                el?.showPicker?.();
              }}
            >
              <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
              <input
                id="endDateInput"
                type="date"
                value={endDate}
                onClick={handleOpenDatePicker}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-rose-500/50"
              />
            </div>
          </div>
        </div>

        {/* Quick Date Range Preset Buttons */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-center space-x-1.5">
            <span className="text-[11px] font-medium text-slate-400 mr-1">Hızlı Tarih:</span>
            <button
              onClick={() => setPresetDateRange('today')}
              className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-[11px] font-medium transition-colors"
            >
              Bugün
            </button>
            <button
              onClick={() => setPresetDateRange('yesterday')}
              className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-[11px] font-medium transition-colors"
            >
              Dün
            </button>
            <button
              onClick={() => setPresetDateRange('week')}
              className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-[11px] font-medium transition-colors"
            >
              Son 7 Gün
            </button>
            <button
              onClick={() => setPresetDateRange('month')}
              className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-[11px] font-medium transition-colors"
            >
              Bu Ay
            </button>
            <button
              onClick={() => setPresetDateRange('all')}
              className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 text-rose-600 dark:text-rose-300 rounded-lg text-[11px] font-medium transition-colors"
            >
              Tüm Zamanlar
            </button>
          </div>

          <div className="text-xs text-slate-400 font-medium">
            Toplam <span className="font-bold text-slate-700 dark:text-slate-200">{orders.length}</span> sipariş listelendi
          </div>
        </div>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 flex items-center justify-center space-x-3">
          <div className="w-6 h-6 border-3 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm font-medium text-slate-500">Siparişler yükleniyor...</span>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-rose-50 dark:bg-rose-950/50 text-rose-500 mx-auto flex items-center justify-center text-xl">
            📦
          </div>
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">Aranan Kriterlerde Sipariş Bulunamadı</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
            Filtreleme kriterlerinizi veya arama kelimenizi değiştirerek tekrar deneyebilirsiniz.
          </p>
        </div>
      ) : (
        <Table
          headers={['Sipariş No', 'Gönderen (Müşteri)', 'Alıcı', 'Ürün', 'Teslim Tarihi', 'Ön İzleme', 'Ödeme', 'Toplam', 'Durum']}
          data={tableData}
          renderCell={(item, header) => {
            // 1. Sipariş No Column (with copy button)
            if (header === 'Sipariş No') {
              return (
                <div className="flex items-center space-x-1.5 group">
                  <span className="font-extrabold text-slate-900 dark:text-slate-100 text-xs">
                    {item.orderNo}
                  </span>
                  <button
                    onClick={(e) => handleCopyOrderNo(e, item.orderNo)}
                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded transition-colors opacity-70 group-hover:opacity-100"
                    title="Sipariş No Kopyala"
                  >
                    {copiedId === item.orderNo ? (
                      <Check size={13} className="text-emerald-500 font-bold" />
                    ) : (
                      <Copy size={13} />
                    )}
                  </button>
                </div>
              );
            }

            // 2. Gönderen (Müşteri) Column - Indigo / Slate styled badge
            if (header === 'Gönderen (Müşteri)') {
              return (
                <div className="flex flex-col space-y-0.5">
                  <span className="inline-flex items-center space-x-1 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded-lg text-xs font-bold w-fit">
                    <span>👤</span>
                    <span className="truncate max-w-[140px]">{item.senderName}</span>
                  </span>
                  {item.senderPhone && (
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                      {item.senderPhone}
                    </span>
                  )}
                </div>
              );
            }

            // 3. Alıcı Column - Rose / Pink styled badge
            if (header === 'Alıcı') {
              return (
                <div className="flex flex-col space-y-0.5">
                  <span className="inline-flex items-center space-x-1 bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 px-2 py-0.5 rounded-lg text-xs font-bold w-fit">
                    <span>🌸</span>
                    <span className="truncate max-w-[140px]">{item.recipientName}</span>
                  </span>
                  {item.recipientPhone && (
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                      {item.recipientPhone}
                    </span>
                  )}
                </div>
              );
            }

            // 4. Ürün Column
            if (header === 'Ürün') {
              return (
                <div className="flex flex-col space-y-0.5 max-w-[160px]">
                  <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate" title={item.itemTitle}>
                    {item.itemTitle}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {item.itemCount}
                  </span>
                </div>
              );
            }

            // 5. Teslim Tarihi Column - Highlighted status badge
            if (header === 'Teslim Tarihi') {
              if (item.isDelivered) {
                return (
                  <span className="inline-flex items-center space-x-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 px-2.5 py-1 rounded-xl text-xs font-extrabold border border-emerald-300/60 dark:border-emerald-800">
                    <span>✅</span>
                    <span>Teslim Edildi ({item.deliveryDateText})</span>
                  </span>
                );
              }
              return (
                <div className="flex flex-col space-y-0.5">
                  <span className="inline-flex items-center space-x-1 bg-amber-100 text-amber-900 dark:bg-amber-950/70 dark:text-amber-300 px-2.5 py-1 rounded-xl text-xs font-extrabold border border-amber-300/60 dark:border-amber-800 w-fit">
                    <span>⚠️</span>
                    <span>Teslim Edildi Değil</span>
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Hedef: {item.deliveryDateText}
                  </span>
                </div>
              );
            }

            // 6. Ön İzleme (Fotoğraf) Column
            if (header === 'Ön İzleme') {
              if (item.hasPreview) {
                return (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewModalOrder(item.rawOrder);
                    }}
                    className="inline-flex items-center space-x-1.5 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/60 dark:hover:bg-emerald-900/80 text-emerald-700 dark:text-emerald-300 px-2.5 py-1 rounded-xl text-xs font-bold border border-emerald-200 dark:border-emerald-800 transition-colors"
                  >
                    <Camera size={13} className="text-emerald-600 dark:text-emerald-400" />
                    <span>Fotoğraf Var</span>
                  </button>
                );
              }
              return (
                <span className="inline-flex items-center space-x-1 bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 px-2 py-0.5 rounded-lg text-xs font-medium">
                  <span>Bekliyor</span>
                </span>
              );
            }

            // 7. Ödeme Column
            if (header === 'Ödeme') {
              return (
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  {item.payment}
                </span>
              );
            }

            // 8. Toplam Column
            if (header === 'Toplam') {
              return (
                <span className="text-xs font-extrabold text-slate-900 dark:text-slate-100">
                  {item.total}
                </span>
              );
            }

            // 9. Durum Column
            if (header === 'Durum') {
              const status = item.statusRaw as string;
              const colors = STATUS_COLORS[status] || STATUS_COLORS.pending;
              return (
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${colors.badge}`}>
                  {item.status}
                </span>
              );
            }

            return '';
          }}
          actions={(item) => (
            <button
              onClick={() => navigate(`/admin/orders/${item.id}`)}
              className="p-2 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-xl transition-colors"
              title="Sipariş Detayına Git"
            >
              <Eye size={16} />
            </button>
          )}
        />
      )}

      {/* Lightbox Photo Preview Modal */}
      {previewModalOrder && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setPreviewModalOrder(null)}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <ImageIcon size={18} className="text-rose-400" />
                <h3 className="text-sm font-bold">
                  Sipariş Onay Fotoğrafı ({previewModalOrder.orderNo || previewModalOrder.id})
                </h3>
              </div>
              <button
                onClick={() => setPreviewModalOrder(null)}
                className="p-1 hover:bg-white/20 rounded-lg text-slate-300 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Image Body */}
            <div className="p-4 bg-slate-950 flex items-center justify-center min-h-[300px] max-h-[500px] overflow-hidden">
              {previewModalOrder.orderPreview?.photoUrl || previewModalOrder.orderPreview?.photoUrls?.[0] ? (
                <img
                  src={previewModalOrder.orderPreview?.photoUrl || previewModalOrder.orderPreview?.photoUrls?.[0] || ''}
                  alt="Sipariş Ön İzleme"
                  className="max-h-[460px] w-auto object-contain rounded-2xl shadow-md"
                />
              ) : (
                <div className="text-slate-400 text-xs">Fotoğraf yüklenemedi.</div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                {previewModalOrder.orderPreview?.uploadedAt ? (
                  <span>Yüklenme: {new Date(previewModalOrder.orderPreview.uploadedAt).toLocaleString('tr-TR')}</span>
                ) : (
                  <span>Fotoğraf Hazır</span>
                )}
              </div>
              <button
                onClick={() => {
                  const id = previewModalOrder.id;
                  setPreviewModalOrder(null);
                  navigate(`/admin/orders/${id}`);
                }}
                className="px-4 py-2 bg-gradient-to-r from-rose-600 to-pink-600 text-white text-xs font-bold rounded-xl shadow-md hover:from-rose-700 hover:to-pink-700 transition-all flex items-center space-x-1.5"
              >
                <Eye size={14} />
                <span>Sipariş Detayına Git</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
